import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";

// Sloupce, které lze skrývat na hlavní stránce. Klíče musí odpovídat
// podmínkám hiddenColumns.includes(...) v App.js.
const CONFIGURABLE_COLUMNS = [
  { key: "ico", label: "IČO" },
  { key: "operator", label: "Operátor" },
  { key: "bmsl", label: "BMSL" },
  { key: "forecast", label: "FC" },
  { key: "expectedCommission", label: "Očekávaná provize" },
  { key: "opportunity", label: "Příležitost" },
  { key: "prm", label: "PRM" },
  { key: "status", label: "Stav" },
];

export default function ColumnSettingsModal({ isDarkMode, userId, onClose, onSaved }) {
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/column-config?userId=${userId || ""}`);
      if (!res.ok) throw new Error("Chyba serveru");
      const data = await res.json();
      setHiddenColumns(Array.isArray(data.hiddenColumns) ? data.hiddenColumns : []);
    } catch (e) {
      setError("Nepodařilo se načíst nastavení: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleColumn = (key) => {
    setHiddenColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/column-config?userId=${userId || ""}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hiddenColumns, userId }),
      });
      if (!res.ok) throw new Error("Chyba serveru");
      const data = await res.json();
      const saved = Array.isArray(data.hiddenColumns) ? data.hiddenColumns : hiddenColumns;
      localStorage.setItem("hiddenColumns", JSON.stringify(saved));
      if (onSaved) onSaved(saved);
      if (onClose) onClose();
    } catch (e) {
      setError("Chyba při ukládání: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const panel = isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900";
  const border = isDarkMode ? "border-gray-700" : "border-gray-200";
  const rowHover = isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className={`${panel} w-full max-w-md rounded-2xl shadow-2xl border ${border} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-6 py-4 border-b ${border} flex items-center justify-between`}>
          <h2 className="text-lg font-bold">🧩 Správa sloupců</h2>
          <button
            onClick={onClose}
            className={`text-2xl leading-none ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
          >
            ×
          </button>
        </div>

        <div className="px-6 py-4">
          <p className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            Zaškrtnuté sloupce se zobrazí na hlavní stránce. Odškrtnuté sloupce budou
            skryté. Nastavení platí pro celého partnera.
          </p>

          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg text-sm bg-red-100 text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-gray-400">Načítám...</div>
          ) : (
            <div className="space-y-1">
              {CONFIGURABLE_COLUMNS.map((col) => {
                const visible = !hiddenColumns.includes(col.key);
                return (
                  <label
                    key={col.key}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer ${rowHover}`}
                  >
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={() => toggleColumn(col.key)}
                      className="w-5 h-5 accent-blue-600"
                    />
                    <span className="text-sm font-medium">{col.label}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className={`px-6 py-4 border-t ${border} flex justify-end gap-3`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
          >
            Zrušit
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition disabled:opacity-60"
          >
            {saving ? "Ukládám..." : "Uložit"}
          </button>
        </div>
      </div>
    </div>
  );
}
